import React, { useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CommonTable from 'app/components/Common/CommonTable';
import {
  TableColumns,
  TableRow as TableRowType,
  TableRowCellsData,
} from 'app/components/Common/CommonTable/type';
import i18next from 'i18next';
import { themeContext } from 'app/components/Context/ToggleThemeContext';
import {
  Leg,
  PositionData,
  PositionWithExpiryData,
  isString,
  isNumber,
  OptionChainTableRow,
  StrikeData,
  isStrikeData,
  SymbolOptionChainData,
  GetOptionChainResultDataType,
} from './types';
import {
  convertPositionsToApiFormat,
  updateLocalStoragePositions,
} from './legsUtils';
import { updateGlobalPositions } from '../Datafeeds/datafeed';

// Type declaration for TradingView widget
declare global {
  interface Window {
    tvWidget?: {
      activeChart?: () => {
        resetData?: () => void;
        symbol?: () => string;
        resolution?: () => string;
        getVisibleRange?: () => { from: number; to: number };
        setVisibleRange?: (range: { from: number; to: number }) => void;
      };
      setSymbol?: (
        symbol: string,
        resolution: string,
        callback?: () => void,
      ) => void;
    };
  }
}

interface OptionChainPopupProps {
  isOpen: boolean;
  onClose: () => void;
  safeSymbol: string;
  safeSpotPrice: number;
  optionChainData: GetOptionChainResultDataType | undefined;
  positions: PositionWithExpiryData;
  optionTabMetaData: { optionExpiry?: string } | undefined;
  onClearLegs: (() => void) | undefined;
  onLegAdd: ((leg: Leg) => void) | undefined;
  onPositionUpdate: ((positions: PositionWithExpiryData) => void) | undefined;
  handleOptionClick: (
    positionData: PositionData,
    contractType: string,
    strike: string,
    isMinusClick?: boolean,
  ) => void;
  onPositionFlagTrigger?: () => void;
}

const OptionChainPopup: React.FC<OptionChainPopupProps> = ({
  isOpen,
  onClose,
  safeSymbol,
  safeSpotPrice,
  optionChainData,
  positions,
  optionTabMetaData,
  onClearLegs,
  onLegAdd,
  onPositionUpdate,
  handleOptionClick,
  onPositionFlagTrigger,
}) => {
  const theme = useTheme();
  const { isDarkTheme } = React.useContext(themeContext);
  const tableRef = useRef<HTMLDivElement>(null);

  // Create option chain table data
  const optionChainTableData = useMemo(() => {
    if (!optionChainData?.candles) return [];

    const strikes = Object.keys(optionChainData.candles).sort(
      (a, b) => Number(a) - Number(b),
    );
    const currentStrike = safeSpotPrice;
    const currentExpiry = optionTabMetaData?.optionExpiry;

    // Calculate ATM strike using simulator logic
    const atmStrike =
      strikes.length > 0
        ? strikes
            .map(Number)
            .reduce((prev, curr) =>
              Math.abs(curr - currentStrike) < Math.abs(prev - currentStrike)
                ? curr
                : prev,
            )
        : currentStrike;

    return strikes.map(strike => {
      const strikeData = optionChainData?.candles?.[strike];
      const safeStrikeData = isStrikeData(strikeData)
        ? strikeData
        : ({} as StrikeData);
      const strikeNum = Number(strike);
      const isATM = atmStrike === strikeNum;
      const isITM = atmStrike >= strikeNum;
      const isOTM = atmStrike <= strikeNum;

      // Use positions prop directly - get positions for current expiry
      const positionsForCurrentExpiry = currentExpiry
        ? positions[currentExpiry] || {}
        : {};
      const cePosition = positionsForCurrentExpiry[`CE_${strike}`];
      const pePosition = positionsForCurrentExpiry[`PE_${strike}`];

      return {
        strike,
        ceLTP: safeStrikeData.CE?.close || 0,
        peLTP: safeStrikeData.PE?.close || 0,
        isATM,
        isITM,
        isOTM,
        cePosition,
        pePosition,
      };
    });
  }, [
    optionChainData?.candles,
    safeSpotPrice,
    optionTabMetaData?.optionExpiry,
    positions,
  ]);

  // Create data for CommonTable with positions from legs
  const optionChainTableDataForCommonTable = useMemo(() => {
    const columns: TableColumns[] = [
      { text: i18next.t('simulator.table_column.lots'), align: 'center' },
      { text: i18next.t('simulator.table_column.strike'), align: 'center' },
      { text: i18next.t('simulator.table_column.lots'), align: 'center' },
    ];

    // Calculate ATM strike using simulator logic
    const strikes = optionChainTableData.map(row => Number(row.strike));
    const atmStrike =
      strikes.length > 0
        ? strikes.reduce((prev, curr) =>
            Math.abs(curr - safeSpotPrice) < Math.abs(prev - safeSpotPrice)
              ? curr
              : prev,
          )
        : safeSpotPrice;

    // Get positions for current expiry
    const currentExpiry = optionTabMetaData?.optionExpiry;
    const positionsForCurrentExpiry = currentExpiry
      ? positions[currentExpiry] || {}
      : {};

    const rows: TableRowType<OptionChainTableRow>[] = optionChainTableData.map(
      row => {
        const ceStrikeData = optionChainData?.candles?.[row.strike];
        const peStrikeData = optionChainData?.candles?.[row.strike];
        const safeCeData = isStrikeData(ceStrikeData)
          ? ceStrikeData.CE || {}
          : ({} as SymbolOptionChainData);
        const safePeData = isStrikeData(peStrikeData)
          ? peStrikeData.PE || {}
          : ({} as SymbolOptionChainData);

        const strike = Number(row.strike);
        const isATM = atmStrike === strike;
        const isITM = atmStrike >= strike;
        const isOTM = atmStrike <= strike;
        const atmBg = theme?.component?.optionChainColors?.atmBg || '#e3f2fd';
        const itmBg = theme?.component?.optionChainColors?.itmBg || '#e8f5e8';

        // Use positions prop directly - get positions for current expiry
        const cePosition = positionsForCurrentExpiry[`CE_${row.strike}`];
        const pePosition = positionsForCurrentExpiry[`PE_${row.strike}`];

        const rowCellsData: TableRowCellsData[] = [
          {
            content: (
              <Box
                display="flex"
                gap={0.5}
                justifyContent="center"
                alignItems="center"
                className={cePosition ? '' : 'hover-visible'}
                sx={{
                  minWidth: '120px',
                  maxWidth: '120px',
                  width: '120px',
                }}
              >
                {/* Display lots for both Long and Short positions */}
                {cePosition && cePosition.lots > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      minWidth: '20px',
                      textAlign: 'center',
                      fontSize: '0.7rem',
                    }}
                  >
                    {cePosition.lots}X
                  </Typography>
                )}

                {/* Minus Button for both Long and Short positions */}
                {cePosition?.lots > 0 && (
                  <Box
                    sx={{
                      borderRadius: 0.7,
                      borderWidth: 1,
                      borderStyle: 'solid',
                      borderColor:
                        cePosition.direction === 'long'
                          ? 'success.main'
                          : 'error.main',
                      mx: 0.3,
                      my: 0.5,
                      cursor: 'pointer',
                      height: '1.2rem',
                      width: '1.2rem',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexShrink: 0,
                    }}
                    onClick={() =>
                      handleOptionClick(
                        {
                          direction: cePosition.direction,
                          lots: Math.max(1, cePosition.lots - 1), // Decrease lots, minimum 1
                          premium: row.ceLTP,
                          contractType: 'CE',
                          strike: row.strike,
                          iv: safeCeData.iv,
                          entryTime: new Date().toISOString(),
                          contractDetails: safeCeData,
                        },
                        'CE',
                        row.strike,
                        true,
                      )
                    }
                  >
                    <Typography sx={{ fontSize: 14 }}>-</Typography>
                  </Box>
                )}

                {/* Buy Button */}
                <Box
                  sx={{
                    borderRadius: 0.7,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: 'success.main',
                    mx: 0.3,
                    my: 0.5,
                    cursor: 'pointer',
                    height: '1.2rem',
                    width: '1.2rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor:
                      cePosition?.direction === 'long'
                        ? 'success.main'
                        : 'transparent',
                    flexShrink: 0,
                    '&:hover': {
                      backgroundColor:
                        cePosition?.direction === 'long'
                          ? 'success.dark'
                          : 'success.light',
                    },
                  }}
                  onClick={() =>
                    handleOptionClick(
                      {
                        direction: 'long',
                        lots: 1,
                        premium: row.ceLTP,
                        contractType: 'CE',
                        strike: row.strike,
                        iv: safeCeData.iv,
                        entryTime: new Date().toISOString(),
                        contractDetails: safeCeData,
                      },
                      'CE',
                      row.strike,
                      false,
                    )
                  }
                >
                  <Typography
                    sx={{
                      color:
                        cePosition?.direction === 'long'
                          ? 'white'
                          : 'success.main',
                      fontSize: 14,
                    }}
                  >
                    B
                  </Typography>
                </Box>

                {/* Sell Button */}
                <Box
                  sx={{
                    borderRadius: 0.7,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: 'error.main',
                    mx: 0.3,
                    my: 0.5,
                    cursor: 'pointer',
                    height: '1.2rem',
                    width: '1.2rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor:
                      cePosition?.direction === 'short'
                        ? 'error.main'
                        : 'transparent',
                    flexShrink: 0,
                    '&:hover': {
                      backgroundColor:
                        cePosition?.direction === 'short'
                          ? 'error.dark'
                          : 'error.light',
                    },
                  }}
                  onClick={() =>
                    handleOptionClick(
                      {
                        direction: 'short',
                        lots: 1,
                        premium: row.ceLTP,
                        contractType: 'CE',
                        strike: row.strike,
                        iv: safeCeData.iv,
                        entryTime: new Date().toISOString(),
                        contractDetails: safeCeData,
                      },
                      'CE',
                      row.strike,
                      false,
                    )
                  }
                >
                  <Typography
                    sx={{
                      color:
                        cePosition?.direction === 'short'
                          ? 'white'
                          : 'error.main',
                      fontSize: 14,
                    }}
                  >
                    S
                  </Typography>
                </Box>
              </Box>
            ),
            align: 'center',
            backgroudColor: isATM ? atmBg : isITM ? itmBg : '',
          },
          {
            content: (
              <Box
                sx={{ minWidth: '100px', maxWidth: '100px', width: '100px' }}
              >
                <Typography
                  variant="body2"
                  fontWeight={isATM ? 'bold' : 'normal'}
                  color={isATM ? 'primary.main' : 'text.primary'}
                >
                  {row.strike}
                </Typography>
              </Box>
            ),
            align: 'center',
            fontWeight: isATM ? 700 : 400,
            backgroudColor: isATM ? atmBg : '',
          },
          {
            content: (
              <Box
                display="flex"
                gap={0.5}
                justifyContent="center"
                alignItems="center"
                className={pePosition ? '' : 'hover-visible'}
                sx={{
                  minWidth: '120px',
                  maxWidth: '120px',
                  width: '120px',
                }}
              >
                {/* Display lots for both Long and Short positions */}
                {pePosition && pePosition.lots > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      minWidth: '20px',
                      textAlign: 'center',
                      fontSize: '0.7rem',
                    }}
                  >
                    {pePosition.lots}X
                  </Typography>
                )}

                {/* Minus Button for both Long and Short positions */}
                {pePosition?.lots > 0 && (
                  <Box
                    sx={{
                      borderRadius: 0.7,
                      borderWidth: 1,
                      borderStyle: 'solid',
                      borderColor:
                        pePosition.direction === 'long'
                          ? 'success.main'
                          : 'error.main',
                      mx: 0.3,
                      my: 0.5,
                      cursor: 'pointer',
                      height: '1.2rem',
                      width: '1.2rem',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexShrink: 0,
                    }}
                    onClick={() =>
                      handleOptionClick(
                        {
                          direction: pePosition.direction,
                          lots: Math.max(1, pePosition.lots - 1), // Decrease lots, minimum 1
                          premium: row.peLTP,
                          contractType: 'PE',
                          strike: row.strike,
                          iv: safePeData.iv,
                          entryTime: new Date().toISOString(),
                          contractDetails: safePeData,
                        },
                        'PE',
                        row.strike,
                        true,
                      )
                    }
                  >
                    <Typography sx={{ fontSize: 14 }}>-</Typography>
                  </Box>
                )}

                {/* Buy Button */}
                <Box
                  sx={{
                    borderRadius: 0.7,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: 'success.main',
                    mx: 0.3,
                    my: 0.5,
                    cursor: 'pointer',
                    height: '1.2rem',
                    width: '1.2rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor:
                      pePosition?.direction === 'long'
                        ? 'success.main'
                        : 'transparent',
                    flexShrink: 0,
                    '&:hover': {
                      backgroundColor:
                        pePosition?.direction === 'long'
                          ? 'success.dark'
                          : 'success.light',
                    },
                  }}
                  onClick={() =>
                    handleOptionClick(
                      {
                        direction: 'long',
                        lots: 1,
                        premium: row.peLTP,
                        contractType: 'PE',
                        strike: row.strike,
                        iv: safePeData.iv,
                        entryTime: new Date().toISOString(),
                        contractDetails: safePeData,
                      },
                      'PE',
                      row.strike,
                      false,
                    )
                  }
                >
                  <Typography
                    sx={{
                      color:
                        pePosition?.direction === 'long'
                          ? 'white'
                          : 'success.main',
                      fontSize: 14,
                    }}
                  >
                    B
                  </Typography>
                </Box>

                {/* Sell Button */}
                <Box
                  sx={{
                    borderRadius: 0.7,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: 'error.main',
                    mx: 0.3,
                    my: 0.5,
                    cursor: 'pointer',
                    height: '1.2rem',
                    width: '1.2rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor:
                      pePosition?.direction === 'short'
                        ? 'error.main'
                        : 'transparent',
                    flexShrink: 0,
                    '&:hover': {
                      backgroundColor:
                        pePosition?.direction === 'short'
                          ? 'error.dark'
                          : 'error.light',
                    },
                  }}
                  onClick={() =>
                    handleOptionClick(
                      {
                        direction: 'short',
                        lots: 1,
                        premium: row.peLTP,
                        contractType: 'PE',
                        strike: row.strike,
                        iv: safePeData.iv,
                        entryTime: new Date().toISOString(),
                        contractDetails: safePeData,
                      },
                      'PE',
                      row.strike,
                      false,
                    )
                  }
                >
                  <Typography
                    sx={{
                      color:
                        pePosition?.direction === 'short'
                          ? 'white'
                          : 'error.main',
                      fontSize: 14,
                    }}
                  >
                    S
                  </Typography>
                </Box>
              </Box>
            ),
            align: 'center',
            backgroudColor: isATM ? atmBg : isOTM ? itmBg : '',
          },
        ];

        return {
          rowCellsData,
          rowData: row,
          pt: 0.5,
          pb: 0.5,
          visibleOnHover: true,
          sx: { className: 'hoverable-row' },
        };
      },
    );

    return { rows, columns };
  }, [
    optionChainTableData,
    safeSpotPrice,
    theme,
    optionChainData,
    positions,
    handleOptionClick,
    optionTabMetaData?.optionExpiry,
  ]);

  // Auto-scroll to ATM row in popup
  useEffect(() => {
    if (isOpen && optionChainTableDataForCommonTable.rows.length > 1) {
      // Use the same ATM calculation as simulator
      const strikes = optionChainTableDataForCommonTable.rows.map(row =>
        Number(row.rowData.strike),
      );
      const atmStrike =
        strikes.length > 0
          ? strikes.reduce((prev, curr) =>
              Math.abs(curr - safeSpotPrice) < Math.abs(prev - safeSpotPrice)
                ? curr
                : prev,
            )
          : safeSpotPrice;

      const atmRowIndex = strikes.findIndex(strike => strike === atmStrike);

      if (atmRowIndex !== -1) {
        setTimeout(() => {
          // Use the same approach as OptionCustomTable
          const tableContainer = document.querySelector('.scrollThis');
          if (tableContainer) {
            // Try to get actual row height from DOM
            const firstRow = tableContainer.querySelector('tbody tr');
            const actualRowHeight = firstRow
              ? (firstRow as HTMLElement).offsetHeight
              : 40;

            // Try to get actual header height from DOM
            const headerRow = tableContainer.querySelector('thead tr');
            const actualHeaderHeight = headerRow
              ? (headerRow as HTMLElement).offsetHeight
              : 48;

            const scrollTop =
              atmRowIndex * actualRowHeight - actualHeaderHeight;

            tableContainer.scrollTo({
              top: Math.max(0, scrollTop),
              behavior: 'smooth',
            });
          }
        }, 100);
      }
    }
  }, [isOpen, optionChainTableDataForCommonTable, safeSpotPrice]);

  const handleConfirm = () => {
    // Store positions in localStorage
    const currentExpiry = optionTabMetaData?.optionExpiry;
    const hasAnyPositions = Object.values(positions).some(
      expiryPositions => Object.keys(expiryPositions).length > 0,
    );

    if (currentExpiry && hasAnyPositions) {
      // Use the new localStorage sync function
      updateLocalStoragePositions(positions, safeSymbol, currentExpiry);

      // Sync legs table with current positions
      if (onClearLegs && onLegAdd) {
        // Clear existing legs first
        onClearLegs();

        // Create new legs from all positions (all expiries)
        Object.entries(positions).forEach(([expiry, expiryPositions]) => {
          Object.entries(expiryPositions).forEach(
            ([positionKey, positionData]) => {
              const [contractType, strike] = positionKey.split('_');
              const newLeg: Leg = {
                id: `${contractType}_${strike}_${Date.now()}`,
                direction: positionData.direction,
                expiry: expiry,
                strike: strike,
                lots: positionData.lots,
                ltp: positionData.premium || 0,
                entryTime: positionData.entryTime || new Date().toISOString(),
                contractType: contractType,
                entryPrice: positionData.premium || 0,
                isActive: true,
                iv: positionData.iv,
                premium: positionData.premium,
                contractDetails: positionData.contractDetails,
              };
              onLegAdd(newLeg);
            },
          );
        });

        // NEW: Trigger position flag when confirm is clicked
        if (onPositionFlagTrigger) {
          onPositionFlagTrigger();
        }
      }
    }

    // Reset TradingView chart
    if (window.tvWidget?.activeChart) {
      try {
        const chart = window.tvWidget.activeChart();

        // Update global positions for chart
        const currentExpiry = optionTabMetaData?.optionExpiry;
        // Check if there are any positions across all expiries
        const hasAnyPositions = Object.values(positions).some(
          expiryPositions => Object.keys(expiryPositions).length > 0,
        );

        if (hasAnyPositions) {
          // Convert positions to API format
          const apiFormatObject = convertPositionsToApiFormat(
            positions,
            safeSymbol,
          );

          // Update global positions in datafeed
          updateGlobalPositions(apiFormatObject.positions);

          // Reset chart data
          if (chart.resetData) {
            chart.resetData();
          }

          // Force chart to reload with new positions
          setTimeout(() => {
            if (window.tvWidget?.setSymbol) {
              const currentResolution = chart?.resolution?.();

              if (currentResolution) {
                // Change chart symbol to match positions symbol
                const targetSymbol = apiFormatObject.positions.symbol;

                window.tvWidget.setSymbol(
                  targetSymbol,
                  currentResolution,
                  () => {
                    // Chart reloaded with positions
                  },
                );
              }
            }
          }, 500);
        } else {
          // Clear positions if none exist
          updateGlobalPositions({
            symbol: `NSE:${safeSymbol}`,
            contracts: {},
          });
        }
      } catch (error) {
        // Error resetting chart
      }
    } else {
      // TradingView widget not available
    }

    // Close popup
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Option Chain - {safeSymbol}</Typography>
          <Typography variant="body2" color="text.secondary">
            Spot: {safeSpotPrice}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <div ref={tableRef}>
          <CommonTable
            rows={optionChainTableDataForCommonTable.rows}
            columns={optionChainTableDataForCommonTable.columns}
            containerStyle={{
              height: 'calc(100vh - 300px)',
              overflow: 'auto',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': {
                width: '8px',
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: theme.palette.grey[100],
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: theme.palette.grey[400],
                borderRadius: '4px',
                '&:hover': {
                  background: theme.palette.grey[500],
                },
              },
              '&::-webkit-scrollbar-corner': {
                background: theme.palette.grey[100],
              },
              // Compact table styling
              '& .MuiTable-root': {
                borderCollapse: 'separate',
                borderSpacing: 0,
                minWidth: '100%',
                tableLayout: 'fixed',
              },
              '& .MuiTableHead-root': {
                position: 'sticky',
                top: 0,
                zIndex: 1,
                '& .MuiTableRow-root': {
                  backgroundColor: isDarkTheme
                    ? theme.palette.grey[50]
                    : theme.palette.grey[100],
                  '& .MuiTableCell-root': {
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    color: theme.palette.text.primary,
                    padding: '8px 6px',
                    backgroundColor: isDarkTheme
                      ? theme.palette.grey[50]
                      : theme.palette.grey[200],
                    '&:first-of-type': {
                      borderTopLeftRadius: '8px',
                      width: '120px',
                      minWidth: '120px',
                      maxWidth: '120px',
                    },
                    '&:nth-of-type(2)': {
                      width: '100px',
                      minWidth: '100px',
                      maxWidth: '100px',
                    },
                    '&:last-of-type': {
                      borderTopRightRadius: '8px',
                      width: '120px',
                      minWidth: '120px',
                      maxWidth: '120px',
                    },
                  },
                },
              },
              '& .MuiTableBody-root': {
                '& .MuiTableRow-root': {
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                    transform: 'translateY(-1px)',
                    boxShadow: `0 2px 8px ${theme.palette.action.hover}`,
                  },
                  '&:nth-of-type(even)': {
                    backgroundColor: isDarkTheme
                      ? theme.palette.grey[50]
                      : theme.palette.grey[200],
                  },
                  '& .MuiTableCell-root': {
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    padding: '6px 6px',
                    fontSize: '0.8rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    '&:first-of-type': {
                      borderLeft: '3px solid transparent',
                      width: '120px',
                      minWidth: '120px',
                      maxWidth: '120px',
                    },
                    '&:nth-of-type(2)': {
                      width: '100px',
                      minWidth: '100px',
                      maxWidth: '100px',
                    },
                    '&:last-of-type': {
                      width: '120px',
                      minWidth: '120px',
                      maxWidth: '120px',
                    },
                  },
                },
                // Add padding to the last row for better spacing
                '& .MuiTableRow-root:last-child .MuiTableCell-root': {
                  paddingBottom: '16px',
                },
              },
            }}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" color="primary" onClick={handleConfirm}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OptionChainPopup;
